import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import QRCodeGenerator from '../../components/affiliate/QRCodeGenerator';
import AffiliateTheme from '../../styles/AffiliateTheme';
import { useAffiliateTheme } from '../../context/AffiliateThemeContext';

const AffiliateDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
  const [paymentMethodFormData, setPaymentMethodFormData] = useState({
    paymentMethod: 'paypal',
    paypalEmail: '',
    bankName: '',
    bankIban: '',
    bic: '',
    accountHolder: ''
  });
  const [isPaymentFormSubmitting, setIsPaymentFormSubmitting] = useState(false);
  const [paymentFormError, setPaymentFormError] = useState(null);
  const [paymentFormSuccess, setPaymentFormSuccess] = useState(false);
  
  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        fetchAffiliateStats(user);
        fetchPaymentMethod(user);
      } else {
        // Redirect to login if not authenticated
        navigate('/affiliate/login');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  const fetchPaymentMethod = async (user) => {
    try {
      const functions = getFunctions();
      const getAffiliatePaymentMethod = httpsCallable(functions, 'getAffiliatePaymentMethod');
      
      const result = await getAffiliatePaymentMethod();
      
      if (result.data && result.data.success && result.data.paymentMethod) {
        const paymentMethod = result.data.paymentMethod;
        console.log('Payment method details:', JSON.stringify(paymentMethod, null, 2));
        
        // Définir directement le paymentMethod tel que reçu
        setCurrentPaymentMethod(paymentMethod);
        
        // Initialize form data with current payment method
        const newFormData = {
          paymentMethod: paymentMethod.type || 'paypal'
        };
        
        // Add details specific to payment method type
        if (paymentMethod.type === 'paypal') {
          newFormData.paypalEmail = paymentMethod.paypalEmail || '';
        } else if (paymentMethod.type === 'bank_transfer') {
          newFormData.bankName = paymentMethod.bankName || '';
          newFormData.bankIban = paymentMethod.bankIban || paymentMethod.iban || '';
          newFormData.bic = paymentMethod.bic || '';
          newFormData.accountHolder = paymentMethod.accountHolder || '';
        }
        
        setPaymentMethodFormData(newFormData);
      }
    } catch (error) {
      console.error('Error fetching payment method:', error);
    }
  };
  
  const fetchAffiliateStats = async (user) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const getAffiliateStats = httpsCallable(functions, 'getAffiliateStats');
      
      const result = await getAffiliateStats();
      setStats(result.data);
    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
      setError(t('affiliate.dashboard.error.loadingStats'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    
    setPaymentMethodFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset error and success messages when user changes form values
    setPaymentFormError(null);
    setPaymentFormSuccess(false);
  };
  
  const handlePaymentMethodChange = (method) => {
    setPaymentMethodFormData(prev => ({
      ...prev,
      paymentMethod: method
    }));
    
    // Reset error and success messages when user changes payment method
    setPaymentFormError(null);
    setPaymentFormSuccess(false);
  };
  
  const handlePaymentMethodSubmit = async (e) => {
    e.preventDefault();
    setIsPaymentFormSubmitting(true);
    setPaymentFormError(null);
    setPaymentFormSuccess(false);
    
    try {
      // Validate form data
      if (paymentMethodFormData.paymentMethod === 'paypal' && !paymentMethodFormData.paypalEmail) {
        throw new Error(t('affiliate.dashboard.payments.method.error.paypalEmailRequired'));
      }
      
      if (paymentMethodFormData.paymentMethod === 'bank_transfer') {
        // Vérification complète des détails bancaires
        const missingFields = [];
        
        if (!paymentMethodFormData.bankName) missingFields.push(t('affiliate.dashboard.payments.method.bankName'));
        if (!paymentMethodFormData.bankIban) missingFields.push(t('affiliate.dashboard.payments.method.bankIban'));
        if (!paymentMethodFormData.bic) missingFields.push(t('affiliate.dashboard.payments.method.bic'));
        if (!paymentMethodFormData.accountHolder) missingFields.push(t('affiliate.dashboard.payments.method.accountHolder'));
        
        if (missingFields.length > 0) {
          throw new Error(t('affiliate.dashboard.payments.method.error.bankDetailsRequired') + ': ' + missingFields.join(', '));
        }
        
        // Validation simple de l'IBAN
        const cleanIban = paymentMethodFormData.bankIban.replace(/\s/g, '');
        if (cleanIban.length < 15 || cleanIban.length > 34) {
          throw new Error(t('affiliate.dashboard.payments.method.error.invalidIban'));
        }
        
        // Validation simple du BIC/SWIFT
        const cleanBic = paymentMethodFormData.bic.replace(/\s/g, '');
        if (cleanBic.length !== 8 && cleanBic.length !== 11) {
          throw new Error(t('affiliate.dashboard.payments.method.error.invalidBic'));
        }
      }
      
      // Prepare payment details
      let paymentDetails = {};
      if (paymentMethodFormData.paymentMethod === 'paypal') {
        paymentDetails = { paypalEmail: paymentMethodFormData.paypalEmail };
      } else if (paymentMethodFormData.paymentMethod === 'bank_transfer') {
        paymentDetails = { 
          bankName: paymentMethodFormData.bankName, 
          bankIban: paymentMethodFormData.bankIban,
          bic: paymentMethodFormData.bic,
          accountHolder: paymentMethodFormData.accountHolder
        };
      }
      
      // Call cloud function to update payment method
      const functions = getFunctions();
      const updateAffiliatePaymentMethod = httpsCallable(functions, 'updateAffiliatePaymentMethod');
      
      const result = await updateAffiliatePaymentMethod({
        paymentMethod: paymentMethodFormData.paymentMethod,
        paymentDetails
      });
      
      console.log('Payment method update result:', result);
      
      // Refresh payment method details from server to ensure we display the updated data
      await fetchPaymentMethod(user);
      
      // Set success message
      setPaymentFormSuccess(true);
      
      // Scroll to success message for better UX
      setTimeout(() => {
        document.querySelector('.form-success')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (error) {
      console.error('Error updating payment method:', error);

      // Extraire le message d'erreur en gérant les différents formats possibles
      let errorMessage = t('affiliate.dashboard.payments.method.error.generic');
      let errorCode = 'generic';
      
      // Format des erreurs Firebase avec code et message
      if (error && error.code) {
        // Extraire le code d'erreur (enlever le préfixe 'firebase/' s'il existe)
        errorCode = error.code.includes('/') ? error.code.split('/').pop() : error.code;
      }
      
      // Format d'erreur des Cloud Functions v2 avec détails personnalisés
      if (error && error.details && typeof error.details === 'object' && error.details.code) {
        errorCode = error.details.code;
      } else if (error && error.details && typeof error.details === 'string') {
        // Si details est une simple chaîne, l'utiliser comme message
        errorMessage = error.details;
      }
      
      // Format d'erreur Firebase Function standard
      if (error && error.message) {
        // Utiliser le message comme fallback si pas de traduction
        const fallbackMessage = error.message;
        
        // Essayer d'obtenir la traduction pour le code d'erreur
        errorMessage = t(`affiliate.dashboard.payments.method.error.${errorCode}`, 
                         { defaultValue: fallbackMessage });
      }
      
      setPaymentFormError(errorMessage);
      
      // Scroll to error message for better UX
      setTimeout(() => {
        document.querySelector('.form-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setIsPaymentFormSubmitting(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/affiliate/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const handleGenerateLink = async () => {
    try {
      setIsLoading(true);
      
      const functions = getFunctions();
      const generateReferralCode = httpsCallable(functions, 'generateReferralCode');
      
      await generateReferralCode({
        description: t('affiliate.dashboard.links.defaultDescription')
      });
      
      // Refresh stats to show the new link
      await fetchAffiliateStats(user);
    } catch (error) {
      console.error('Error generating link:', error);
      setError(t('affiliate.dashboard.error.generatingLink'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert(t('affiliate.dashboard.links.copied'));
      })
      .catch(err => {
        console.error('Error copying text:', err);
      });
  };
  
  if (isLoading && !stats) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <p>{t('affiliate.dashboard.loading')}</p>
      </LoadingContainer>
    );
  }

  return (
    <ThemeProvider theme={AffiliateTheme}>
      <PageContainer>
        <DashboardHeader
          as={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <HeaderContent>
            <div>
              <h1>{t('affiliate.dashboard.welcome')}</h1>
              <p>{user?.email}</p>
            </div>
            <LogoutButton onClick={handleLogout}>
              {t('affiliate.dashboard.logout')}
            </LogoutButton>
          </HeaderContent>
        </DashboardHeader>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <DashboardContent>
        <TabsContainer>
          <Tab 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
          >
            {t('affiliate.dashboard.tabs.overview')}
          </Tab>
          <Tab 
            active={activeTab === 'links'} 
            onClick={() => setActiveTab('links')}
          >
            {t('affiliate.dashboard.tabs.links')}
          </Tab>
          <Tab 
            active={activeTab === 'payments'} 
            onClick={() => setActiveTab('payments')}
          >
            {t('affiliate.dashboard.tabs.payments')}
          </Tab>
          <Tab 
            active={activeTab === 'settings'} 
            onClick={() => setActiveTab('settings')}
          >
            {t('affiliate.dashboard.tabs.settings')}
          </Tab>
        </TabsContainer>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <TabContent
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <StatCardsContainer>
              <StatCard>
                <StatValue>${stats.totalEarnings.toFixed(2)}</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.totalEarnings')}</StatLabel>
              </StatCard>
              
              <StatCard>
                <StatValue>${stats.unpaidEarnings.toFixed(2)}</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.unpaidEarnings')}</StatLabel>
              </StatCard>
              
              <StatCard>
                <StatValue>{stats.totalConversions}</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.totalConversions')}</StatLabel>
              </StatCard>
              
              <StatCard>
                <StatValue>{stats.totalClicks}</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.totalClicks')}</StatLabel>
              </StatCard>
              
              <StatCard>
                <StatValue>{stats.conversionRate}%</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.conversionRate')}</StatLabel>
              </StatCard>
              
              <StatCard>
                <StatValue>{stats.last30DaysCount}</StatValue>
                <StatLabel>{t('affiliate.dashboard.stats.last30Days')}</StatLabel>
              </StatCard>
            </StatCardsContainer>
            
            {/* Premium Rate Progress */}
            <PremiumRateSection>
              <h3>
                {stats.hasPremiumRate 
                  ? t('affiliate.dashboard.premium.achieved')
                  : t('affiliate.dashboard.premium.progress')}
              </h3>
              
              <p>
                {stats.hasPremiumRate 
                  ? t('affiliate.dashboard.premium.congratulations', {rate: '$4'})
                  : t('affiliate.dashboard.premium.description', {rate: '$4'})}
              </p>
              
              <ProgressBarContainer>
                <ProgressBarLabel>
                  {t('affiliate.dashboard.premium.progressLabel', {
                    current: stats.progressToTier,
                    target: 100
                  })}
                </ProgressBarLabel>
                <ProgressBar>
                  <ProgressFill width={stats.progressToTier} />
                </ProgressBar>
                <ProgressBarTiers>
                  <span>$2</span>
                  <span>$4</span>
                </ProgressBarTiers>
              </ProgressBarContainer>
              
              <p>
                {t('affiliate.dashboard.premium.currentRate')}: 
                <CurrentRate> 
                  ${stats.currentCommissionRate.toFixed(2)}
                </CurrentRate>
              </p>
            </PremiumRateSection>
          </TabContent>
        )}
        
        {/* Links Tab */}
        {activeTab === 'links' && stats && (
          <TabContent
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <SectionHeader>
              <h2>{t('affiliate.dashboard.links.title')}</h2>
              <GenerateButton 
                onClick={handleGenerateLink} 
                disabled={isLoading || stats.referralCodes.length >= 10}
              >
                {t('affiliate.dashboard.links.generate')}
              </GenerateButton>
            </SectionHeader>
            
            {stats.referralCodes.length === 0 ? (
              <EmptyState>
                <p>{t('affiliate.dashboard.links.empty')}</p>
                <GenerateButton 
                  onClick={handleGenerateLink} 
                  disabled={isLoading}
                >
                  {t('affiliate.dashboard.links.generateFirst')}
                </GenerateButton>
              </EmptyState>
            ) : (
              <LinksTable>
                <thead>
                  <tr>
                    <th>{t('affiliate.dashboard.links.table.code')}</th>
                    <th>{t('affiliate.dashboard.links.table.description')}</th>
                    <th>{t('affiliate.dashboard.links.table.clicks')}</th>
                    <th>{t('affiliate.dashboard.links.table.conversions')}</th>
                    <th>{t('affiliate.dashboard.links.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.referralCodes.map((code) => {
                    const appLink = `https://xenya-face.app/download?ref=${code.code}`;
                    return (
                      <tr key={code.code}>
                        <td>{code.code}</td>
                        <td>{code.description || '-'}</td>
                        <td>{code.clickCount}</td>
                        <td>{code.conversionCount}</td>
                        <td>
                          <ActionButton onClick={() => copyToClipboard(appLink)}>
                            {t('affiliate.dashboard.links.table.copy')}
                          </ActionButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </LinksTable>
            )}
            
            {stats.referralCodes.length > 0 && (
              <>
                <LinkInfoSection>
                  <h3>{t('affiliate.dashboard.links.howToUse.title')}</h3>
                  <p>{t('affiliate.dashboard.links.howToUse.description')}</p>
                  <LinkExample>
                    <strong>{t('affiliate.dashboard.links.howToUse.example')}:</strong>
                    <code>https://xenya-face.app/download?ref=YOUR_CODE</code>
                  </LinkExample>
                </LinkInfoSection>
                
                {/* QR Code Generator */}
                {stats.referralCodes.length > 0 && (
                  <>
                    <QRCodeGenerator 
                      referralCode={stats.referralCodes[0].code}
                      appLink={`https://xenya-face.app/download?ref=${stats.referralCodes[0].code}`}
                    />
                    <AdvancedLinksContainer>
                      <AdvancedLink to="/affiliate/qrcode">
                        {t('affiliate.dashboard.links.advancedQR')}
                      </AdvancedLink>
                      <AdvancedLink to="/affiliate/marketing">
                        {t('affiliate.dashboard.links.marketingResources')}
                      </AdvancedLink>
                    </AdvancedLinksContainer>
                  </>
                )}
              </>
            )}
          </TabContent>
        )}
        
        {/* Payments Tab */}
        {activeTab === 'payments' && stats && (
          <TabContent
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <SectionHeader>
              <h2>{t('affiliate.dashboard.payments.title')}</h2>
            </SectionHeader>
            
            <PaymentInfoCard>
              <h3>{t('affiliate.dashboard.payments.nextPayment')}</h3>
              <PaymentAmount>${stats.unpaidEarnings.toFixed(2)}</PaymentAmount>
              <p>{t('affiliate.dashboard.payments.processDate')}</p>
            </PaymentInfoCard>
            
            <PaymentHistorySection>
              <h3>{t('affiliate.dashboard.payments.history')}</h3>
              <p>{t('affiliate.dashboard.payments.noHistory')}</p>
            </PaymentHistorySection>
            
            <PaymentMethodSection>
              <h3>{t('affiliate.dashboard.payments.method.title')}</h3>
              <p>{t('affiliate.dashboard.payments.method.instructions')}</p>
              
              {/* Current Payment Method Display */}
              {currentPaymentMethod && (
                <CurrentMethodCard>
                  <h4>{t('affiliate.dashboard.payments.method.current')}</h4>
            
                  {currentPaymentMethod.type === 'paypal' ? (
                    <CurrentMethodDetails>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.type')}:</CurrentMethodLabel>
                      <CurrentMethodValue>PayPal</CurrentMethodValue>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.paypalEmail')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{currentPaymentMethod.paypalEmail}</CurrentMethodValue>
                    </CurrentMethodDetails>
                  ) : currentPaymentMethod.type === 'bank_transfer' ? (
                    <CurrentMethodDetails>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.type')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{t('affiliate.dashboard.payments.method.bankTransfer')}</CurrentMethodValue>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.accountHolder')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{currentPaymentMethod.accountHolder}</CurrentMethodValue>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.bankName')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{currentPaymentMethod.bankName}</CurrentMethodValue>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.bankIban')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{currentPaymentMethod.bankIban || currentPaymentMethod.iban}</CurrentMethodValue>
                      <CurrentMethodLabel>{t('affiliate.dashboard.payments.method.bic')}:</CurrentMethodLabel>
                      <CurrentMethodValue>{currentPaymentMethod.bic}</CurrentMethodValue>
                    </CurrentMethodDetails>
                  ) : currentPaymentMethod.type === 'later' ? (
                    <p>{t('affiliate.dashboard.payments.method.notConfigured')}</p>
                  ) : (
                    <p>{t('affiliate.dashboard.payments.method.notConfigured')}</p>
                  )}
                </CurrentMethodCard>
              )}
              
              {/* Update Payment Method Form */}
              <PaymentFormContainer>
                <h4>{t('affiliate.dashboard.payments.method.update')}</h4>
                
                {paymentFormError && <FormError className="form-error">{paymentFormError}</FormError>}
                {paymentFormSuccess && <FormSuccess className="form-success">{t('affiliate.dashboard.payments.method.updateSuccess')}</FormSuccess>}
                
                <form onSubmit={handlePaymentMethodSubmit}>
                  <FormGroup>
                    <FormLabel>{t('affiliate.dashboard.payments.method.selectMethod')}</FormLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          id="paypal"
                          name="paymentMethod"
                          value="paypal"
                          checked={paymentMethodFormData.paymentMethod === 'paypal'}
                          onChange={() => handlePaymentMethodChange('paypal')}
                        />
                        <RadioLabel htmlFor="paypal">PayPal</RadioLabel>
                      </RadioOption>
                      
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          id="bank_transfer"
                          name="paymentMethod"
                          value="bank_transfer"
                          checked={paymentMethodFormData.paymentMethod === 'bank_transfer'}
                          onChange={() => handlePaymentMethodChange('bank_transfer')}
                        />
                        <RadioLabel htmlFor="bank_transfer">
                          {t('affiliate.dashboard.payments.method.bankTransfer')}
                        </RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </FormGroup>
                  
                  {paymentMethodFormData.paymentMethod === 'paypal' && (
                    <FormGroup>
                      <FormLabel htmlFor="paypalEmail">
                        {t('affiliate.dashboard.payments.method.paypalEmail')}
                      </FormLabel>
                      <FormInput
                        type="email"
                        id="paypalEmail"
                        name="paypalEmail"
                        value={paymentMethodFormData.paypalEmail}
                        onChange={handlePaymentFormChange}
                        required
                      />
                    </FormGroup>
                  )}
                  
                  {paymentMethodFormData.paymentMethod === 'bank_transfer' && (
                    <>
                      <FormGroup>
                        <FormLabel htmlFor="accountHolder">
                          {t('affiliate.dashboard.payments.method.accountHolder')}
                        </FormLabel>
                        <FormInput
                          type="text"
                          id="accountHolder"
                          name="accountHolder"
                          value={paymentMethodFormData.accountHolder}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <FormLabel htmlFor="bankName">
                          {t('affiliate.dashboard.payments.method.bankName')}
                        </FormLabel>
                        <FormInput
                          type="text"
                          id="bankName"
                          name="bankName"
                          value={paymentMethodFormData.bankName}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <FormLabel htmlFor="bankIban">
                          {t('affiliate.dashboard.payments.method.bankIban')}
                        </FormLabel>
                        <FormInput
                          type="text"
                          id="bankIban"
                          name="bankIban"
                          value={paymentMethodFormData.bankIban}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <FormLabel htmlFor="bic">
                          {t('affiliate.dashboard.payments.method.bic')}
                        </FormLabel>
                        <FormInput
                          type="text"
                          id="bic"
                          name="bic"
                          value={paymentMethodFormData.bic}
                          onChange={handlePaymentFormChange}
                          required
                        />
                      </FormGroup>
                    </>
                  )}
                  
                  <FormButton type="submit" disabled={isPaymentFormSubmitting}>
                    {isPaymentFormSubmitting
                      ? t('affiliate.dashboard.payments.method.updating')
                      : t('affiliate.dashboard.payments.method.updateButton')}
                  </FormButton>
                </form>
              </PaymentFormContainer>
            </PaymentMethodSection>
          </TabContent>
        )}
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <TabContent
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <SectionHeader>
              <h2>{t('affiliate.dashboard.settings.title')}</h2>
            </SectionHeader>
            
            <SettingsForm>
              <SettingsSection>
                <h3>{t('affiliate.dashboard.settings.account')}</h3>
                <FormGroup>
                  <FormLabel>{t('affiliate.dashboard.settings.email')}</FormLabel>
                  <FormInput
                    type="email"
                    value={user?.email || ''}
                    disabled
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>{t('affiliate.dashboard.settings.changePassword')}</FormLabel>
                  <ActionButton>
                    {t('affiliate.dashboard.settings.passwordButton')}
                  </ActionButton>
                </FormGroup>
              </SettingsSection>
              
              <SettingsSection>
                <h3>{t('affiliate.dashboard.settings.notifications')}</h3>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <CheckboxInput type="checkbox" defaultChecked />
                    {t('affiliate.dashboard.settings.notifyConversions')}
                  </CheckboxLabel>
                  
                  <CheckboxLabel>
                    <CheckboxInput type="checkbox" defaultChecked />
                    {t('affiliate.dashboard.settings.notifyPayments')}
                  </CheckboxLabel>
                  
                  <CheckboxLabel>
                    <CheckboxInput type="checkbox" defaultChecked />
                    {t('affiliate.dashboard.settings.notifyNews')}
                  </CheckboxLabel>
                </CheckboxGroup>
              </SettingsSection>
              
              <SettingsSection>
                <h3>{t('affiliate.dashboard.settings.danger')}</h3>
                <p>{t('affiliate.dashboard.settings.dangerWarning')}</p>
                <DangerButton>
                  {t('affiliate.dashboard.settings.deleteAccount')}
                </DangerButton>
              </SettingsSection>
            </SettingsForm>
          </TabContent>
        )}
      </DashboardContent>
      </PageContainer>
    </ThemeProvider>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const DashboardHeader = styled.header`
  background: linear-gradient(135deg, #1A1A2E 0%, #313866 100%); /* Theme.affiliate.gradients.dark */
  color: white; /* Theme.affiliate.colors.text.contrast.dark */
  padding: 25px 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h1 {
    margin-bottom: 5px;
  }
  
  p {
    opacity: 0.9;
  }
`;

const LogoutButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  color: white; /* Theme.affiliate.colors.text.contrast.dark */
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffe0e0;
  color: #d32f2f;
  padding: 12px 20px;
  border-radius: 5px;
  margin: 20px auto;
  max-width: 1200px;
  font-size: 0.9rem;
`;

const DashboardContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #ddd;
  margin-bottom: 20px;
  overflow-x: auto;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#504099' : 'transparent'};
  color: ${props => props.active ? '#333' : '#666'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  
  &:hover {
    color: #504099; /* Theme.colors.primary */
  }
`;

const TabContent = styled.div`
  padding: 20px 0;
`;

const StatCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const PremiumRateSection = styled.section`
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  
  h3 {
    margin-bottom: 10px;
    color: #333;
  }
  
  p {
    color: #666;
    margin-bottom: 20px;
  }
`;

const ProgressBarContainer = styled.div`
  margin: 20px 0;
`;

const ProgressBarLabel = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  position: relative;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => Math.min(props.width, 100)}%;
  background: linear-gradient(90deg, #504099, #9F73AB); /* Theme.colors primary to accent */
  transition: width 0.5s ease;
`;

const ProgressBarTiers = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  color: #666;
  font-size: 0.8rem;
`;

const CurrentRate = styled.span`
  font-weight: bold;
  color: #504099; /* Theme.colors.primary */
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    color: #333;
  }
`;

const GenerateButton = styled.button`
  background: linear-gradient(135deg, #313866 0%, #504099 100%); /* Theme.colors.gradientPrimary */
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(110, 142, 251, 0.2);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyState = styled.div`
  background: white; /* Theme.affiliate.colors.card */
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05); /* Theme.affiliate.cardShadow */
  text-align: center;
  
  p {
    margin-bottom: 20px;
    color: #666666; /* Theme.affiliate.colors.text.secondary */
  }
`;

const LinksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  th, td {
    padding: 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
    color: #333; /* Ensure text is visible on white background */
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #333;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const ActionButton = styled.button`
  background-color: #504099; /* Theme.colors.primary */
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #313866; /* Theme.colors.secondary */
  }
`;

const LinkInfoSection = styled.div`
  margin-top: 30px;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  h3 {
    color: #333;
    margin-bottom: 10px;
  }
  
  p {
    color: #666;
    margin-bottom: 15px;
  }
`;

const LinkExample = styled.div`
  background-color: #f8f9fa;
  color: #333;
  padding: 15px;
  border-radius: 5px;
  margin-top: 15px;
  
  code {
    display: block;
    margin-top: 5px;
    word-break: break-all;
    color: #504099; /* Theme.colors.primary */
  }
`;

const PaymentInfoCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
  margin-bottom: 30px;
  
  h3 {
    color: #333;
    margin-bottom: 15px;
  }
  
  p {
    color: #666;
  }
`;

const PaymentAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #504099; /* Theme.colors.primary */
  margin-bottom: 15px;
`;

const PaymentHistorySection = styled.section`
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  
  h3 {
    margin-bottom: 15px;
    color: #333;
  }
  
  p {
    color: #666;
    text-align: center;
    padding: 20px 0;
  }
`;

const PaymentMethodSection = styled.section`
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  h3 {
    margin-bottom: 15px;
    color: #333;
  }
  
  p {
    color: #666;
  }
`;

const SettingsForm = styled.form`
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SettingsSection = styled.div`
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  
  h3 {
    margin-bottom: 20px;
    color: #333;
  }
  
  p {
    color: #666;
    margin-bottom: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-weight: normal;
  color: #333;
`;

const CheckboxInput = styled.input`
  margin-right: 10px;
  
  &:checked {
    accent-color: #504099; /* Theme.colors.primary */
  }
`;

const DangerButton = styled.button`
  background-color: #F44336; /* Theme.colors.error */
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  
  p {
    margin-top: 20px;
    color: #666;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #504099; /* Theme.colors.primary */
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AdvancedLinksContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
`;

const AdvancedLink = styled(Link)`
  text-align: center;
  color: #504099; /* Theme.colors.primary */
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
  
  &:hover {
    color: #9F73AB; /* Theme.colors.accent */
    text-decoration: underline;
  }
`;

// Payment Method Components
const CurrentMethodCard = styled.div`
  background: rgba(80, 64, 153, 0.05); /* Light primary color */
  border-left: 4px solid #504099; /* Theme.colors.primary */
  padding: 15px 20px;
  border-radius: 5px;
  margin-top: 20px;
  margin-bottom: 30px;
  
  h4 {
    color: #333;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }
`;

const CurrentMethodDetails = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 15px;
  align-items: center;
`;

const CurrentMethodLabel = styled.span`
  font-weight: 500;
  color: #555;
`;

const CurrentMethodValue = styled.span`
  color: #333;
`;

const PaymentFormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  h4 {
    color: #333;
    margin-bottom: 20px;
    font-size: 1.1rem;
  }
`;

const FormError = styled.div`
  background-color: #ffe0e0;
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const FormSuccess = styled.div`
  background-color: #e0f7e6;
  color: #1e8a3e;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 5px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
`;

const RadioInput = styled.input`
  margin-right: 10px;
  
  &:checked {
    accent-color: #504099; /* Theme.colors.primary */
  }
`;

const RadioLabel = styled.label`
  font-weight: normal;
  color: #333;
`;

const FormButton = styled.button`
  background: linear-gradient(135deg, #313866 0%, #504099 100%); /* Theme.colors.gradientPrimary */
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(110, 142, 251, 0.2);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export default AffiliateDashboardPage;